var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable lines-around-comment */
import { GiphyFetch } from '@giphy/js-fetch-api';
import { Grid } from '@giphy/react-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { createGifSentEvent } from '../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../analytics/functions';
import Input from '../../../base/ui/components/web/Input';
import { sendMessage } from '../../../chat/actions.any';
import { SCROLL_SIZE } from '../../../filmstrip/constants';
import { toggleReactionsMenuVisibility } from '../../../reactions/actions.web';
// @ts-ignore
import { setOverflowMenuVisible } from '../../../toolbox/actions.web';
// @ts-ignore
import { Drawer, JitsiPortal } from '../../../toolbox/components/web';
import { showOverflowDrawer } from '../../../toolbox/functions.web';
import { setGifDrawerVisibility } from '../../actions';
import { formatGifUrlMessage, getGifAPIKey, getGifUrl } from '../../functions';
const OVERFLOW_DRAWER_PADDING = 16;
const useStyles = makeStyles()((theme) => {
    return {
        gifsMenu: {
            width: '100%',
            marginBottom: theme.spacing(2),
            display: 'flex',
            flexDirection: 'column',
            '& div:focus': {
                border: '1px solid red !important',
                boxSizing: 'border-box'
            }
        },
        searchField: {
            marginBottom: theme.spacing(3)
        },
        gifContainer: {
            height: '245px',
            overflowY: 'auto'
        },
        logoContainer: {
            width: `calc(100% - ${SCROLL_SIZE}px)`,
            backgroundColor: '#121119',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            marginTop: theme.spacing(1)
        },
        overflowMenu: {
            padding: theme.spacing(3),
            width: '100%',
            boxSizing: 'border-box'
        },
        gifContainerOverflow: {
            flexGrow: 1
        },
        drawer: {
            display: 'flex',
            height: '100%'
        }
    };
});
/**
 * Gifs menu.
 *
 * @returns {ReactElement}
 */
function GifsMenu() {
    const API_KEY = useSelector(getGifAPIKey);
    const giphyFetch = new GiphyFetch(API_KEY);
    const [searchKey, setSearchKey] = useState();
    const { classes: styles, cx } = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const overflowDrawer = useSelector(showOverflowDrawer);
    const { clientWidth } = useSelector((state) => state['features/base/responsive-ui']);
    const fetchGifs = useCallback((offset = 0) => __awaiter(this, void 0, void 0, function* () {
        const options = {
            rating: 'pg-13',
            limit: 20,
            offset
        };
        if (!searchKey) {
            return yield giphyFetch.trending(options);
        }
        return yield giphyFetch.search(searchKey, options);
    }), [searchKey]);
    const onDrawerClose = useCallback(() => {
        dispatch(setGifDrawerVisibility(false));
        dispatch(setOverflowMenuVisible(false));
    }, []);
    const handleGifClick = useCallback((gif, e) => {
        e === null || e === void 0 ? void 0 : e.stopPropagation();
        const url = getGifUrl(gif);
        sendAnalytics(createGifSentEvent());
        batch(() => {
            dispatch(sendMessage(formatGifUrlMessage(url), true));
            dispatch(toggleReactionsMenuVisibility());
            overflowDrawer && onDrawerClose();
        });
    }, [dispatch, overflowDrawer]);
    const handleGifKeyPress = useCallback((gif, e) => {
        if (e.nativeEvent.keyCode === 13) {
            handleGifClick(gif, null);
        }
    }, [handleGifClick]);
    const handleSearchKeyChange = useCallback(value => {
        setSearchKey(value);
    }, []);
    const handleKeyDown = useCallback(e => {
        if (!document.activeElement) {
            return;
        }
        if (e.keyCode === 38) { // up arrow
            e.preventDefault();
            // if the first gif is focused move focus to the input
            if (document.activeElement.previousElementSibling === null) {
                const element = document.querySelector('.gif-input');
                element === null || element === void 0 ? void 0 : element.focus();
            }
            else {
                const element = document.activeElement.previousElementSibling;
                element === null || element === void 0 ? void 0 : element.focus();
            }
        }
        else if (e.keyCode === 40) { // down arrow
            e.preventDefault();
            // if the input is focused move focus to the first gif
            if (document.activeElement.classList.contains('gif-input')) {
                const element = document.querySelector('.giphy-gif');
                element === null || element === void 0 ? void 0 : element.focus();
            }
            else {
                const element = document.activeElement.nextElementSibling;
                element === null || element === void 0 ? void 0 : element.focus();
            }
        }
    }, []);
    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);
    // For some reason, the Grid component does not do an initial call on mobile.
    // This fixes that.
    useEffect(() => setSearchKey(''), []);
    const onInputKeyPress = useCallback((e) => {
        e.stopPropagation();
    }, []);
    const gifMenu = (React.createElement("div", { className: cx(styles.gifsMenu, overflowDrawer && styles.overflowMenu) },
        React.createElement(Input, { autoFocus: true, className: cx(styles.searchField, 'gif-input'), onChange: handleSearchKeyChange, onKeyPress: onInputKeyPress, placeholder: t('giphy.search'), 
            // eslint-disable-next-line react/jsx-no-bind
            ref: inputElement => {
                inputElement === null || inputElement === void 0 ? void 0 : inputElement.focus();
                setTimeout(() => inputElement === null || inputElement === void 0 ? void 0 : inputElement.focus(), 200);
            }, type: 'text', value: searchKey !== null && searchKey !== void 0 ? searchKey : '' }),
        React.createElement("div", { className: cx(styles.gifContainer, overflowDrawer && styles.gifContainerOverflow) },
            React.createElement(Grid, { columns: 2, fetchGifs: fetchGifs, gutter: 6, hideAttribution: true, key: searchKey, noLink: true, noResultsMessage: t('giphy.noResults'), onGifClick: handleGifClick, onGifKeyPress: handleGifKeyPress, width: overflowDrawer
                    ? clientWidth - (2 * OVERFLOW_DRAWER_PADDING) - SCROLL_SIZE
                    : 320 })),
        React.createElement("div", { className: styles.logoContainer },
            React.createElement("span", null, "Powered by"),
            React.createElement("img", { alt: 'GIPHY Logo', src: 'images/GIPHY_logo.png' }))));
    return overflowDrawer ? (React.createElement(JitsiPortal, null,
        React.createElement(Drawer, { className: styles.drawer, isOpen: true, onClose: onDrawerClose }, gifMenu))) : gifMenu;
}
export default GifsMenu;
